// React Imports
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

// External Libraries
import { useFlags } from "launchdarkly-react-client-sdk";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";

// Local Components
import { LoginLayout } from "../LoginLayout/LoginLayout";
import { McpTenantSelection } from "./McpTenantSelection";

// Hooks & Helpers
import {
  RESTART_HINT,
  isInvalidArgument,
  isPermissionDenied,
  resolveHttpUrl,
} from "./mcpLoginHelpers";
import { getLoginClient } from "sj-ui-toolkit/src/client";
import type { ConnectError } from "sj-ui-toolkit/src/client";
import { useDebounce } from "sj-ui-toolkit/src/hooks";
import { rehydrateUserAuthStatus } from "sj-ui-toolkit/src/utils";
import { themeClasses as T } from "sj-ui-toolkit/src/atlas/utils/themeClasses";

// Styles
import S from "./McpLoginPage.module.scss";

// Routes
import { LoginAppRoutes } from "sj-ui-toolkit/src/constants/routes";

const EmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Same-origin svc-idp MCP OAuth callback; Cognito redirects here after login.
// The FE only relays this well-known path — svc-idp owns all routing decisions.
const OAUTH_CALLBACK_PATH = "/svc/idp/oauth/callback";

// A terminal error means the sign-in link cannot succeed on retry.
function terminalErrorMessage(error: ConnectError): string | null {
  if (isInvalidArgument(error)) {
    return `This sign-in link is invalid or has expired. ${RESTART_HINT}`;
  }
  if (isPermissionDenied(error)) {
    return "MCP sign-in is not enabled for your organization. Please get in touch with your administrator.";
  }
  return null;
}

// Email-first login handoff: the `?state=` leg of the MCP OAuth flow.
function McpEmailLogin() {
  const loginClient = getLoginClient();
  const [searchParams] = useSearchParams();
  const oauthState = searchParams.get("state") ?? "";

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordLogin, setPasswordLogin] = useState(false);
  const [idpCheckInProgress, setIdpCheckInProgress] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pendingSubmitRef = useRef(false);
  // The state-bearing authorize URL from redirect-url, prefetched during
  // detection — submit sends the user to this stored response (same pattern as
  // the normal login page's idpSamlUrlRef)
  const mcpAuthUrlRef = useRef<string | null>(null);
  const [terminalError, setTerminalError] = useState<string | null>(
    oauthState ? null : `This sign-in link is missing required information. ${RESTART_HINT}`
  );

  // Debounced like the normal login page, to avoid a lookup per keystroke
  const debouncedEmail = useDebounce(email, 300);

  const onEmailChange = (value: string) => {
    setEmail(value.trim());
  };

  const navigateTo = (url: string) => {
    const href = resolveHttpUrl(url);
    if (href) {
      window.location.href = href;
      return;
    }
    setTerminalError(`Something went wrong while preparing sign-in. ${RESTART_HINT}`);
  };

  // Password users authenticate here; svc-idp completes the MCP transaction
  // and returns the URL to follow.
  const handlePasswordSignIn = async () => {
    const nonceResponse = await loginClient.generateAuthNonce();
    if (nonceResponse.error || !nonceResponse.data?.nonce) {
      window.toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Unable to continue sign-in. Please try again.",
      });
      setIsSubmitting(false);
      return;
    }

    const authResponse = await loginClient.passwordAuthenticate({
      username: email,
      password,
      nonce: nonceResponse.data.nonce,
      state: oauthState,
    });

    if (authResponse.error) {
      window.toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: authResponse.error.message || "Error during authentication",
      });
      setIsSubmitting(false);
      return;
    }

    if (!authResponse.data.redirect_url) {
      setTerminalError(`Something went wrong while preparing sign-in. ${RESTART_HINT}`);
      return;
    }

    navigateTo(authResponse.data.redirect_url);
  };

  const fetchAuthUrl = async (forEmail: string): Promise<string | null> => {
    const { data, error } = await loginClient.redirect({
      email: forEmail,
      redirect_to: `${window.location.origin}${OAUTH_CALLBACK_PATH}`,
      state: oauthState,
    });

    if (error) {
      const terminal = terminalErrorMessage(error);
      if (terminal) {
        setTerminalError(terminal);
      } else {
        window.toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Unable to continue sign-in. Please try again.",
        });
        setIsSubmitting(false);
      }
      return null;
    }

    if (!data.url) {
      setPasswordLogin(true);
      setIsSubmitting(false);
      return null;
    }

    return data.url;
  };

  const handleSsoSignIn = async () => {
    const url = mcpAuthUrlRef.current ?? (await fetchAuthUrl(email));
    if (url) {
      navigateTo(url);
    }
  };

  useEffect(() => {
    if (!oauthState || !EmailRegex.test(debouncedEmail)) return;

    let cancelled = false;
    const checkForIdpUrl = async () => {
      setIdpCheckInProgress(true);
      mcpAuthUrlRef.current = null;
      try {
        const { data, error } = await loginClient.redirect({
          email: debouncedEmail,
          redirect_to: `${window.location.origin}${OAUTH_CALLBACK_PATH}`,
        });
        if (cancelled) return;

        const isPasswordUser = !!error || !data?.url;
        setPasswordLogin(isPasswordUser);

        if (!isPasswordUser) {
          const url = await fetchAuthUrl(debouncedEmail);
          if (cancelled) return;
          mcpAuthUrlRef.current = url;
        }

        if (pendingSubmitRef.current) {
          pendingSubmitRef.current = false;
          if (isPasswordUser) {
            setIsSubmitting(false);
          } else if (mcpAuthUrlRef.current) {
            navigateTo(mcpAuthUrlRef.current);
          }
        }
      } finally {
        if (!cancelled) {
          setIdpCheckInProgress(false);
        }
      }
    };
    checkForIdpUrl();

    return () => {
      cancelled = true;
    };
  }, [debouncedEmail, oauthState]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!EmailRegex.test(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");

    if (!passwordLogin && (idpCheckInProgress || email !== debouncedEmail)) {
      pendingSubmitRef.current = true;
      setIsSubmitting(true);
      return;
    }

    setIsSubmitting(true);

    if (passwordLogin) {
      await handlePasswordSignIn();
    } else {
      await handleSsoSignIn();
    }
  };

  return (
    <LoginLayout title="Login">
      {terminalError ? (
        <div className="flex flex-col w-full gap-2" role="alert" data-testid="mcp-login-error">
          <div className="text-center">
            <p className="mb-4">{terminalError}</p>
          </div>
        </div>
      ) : (
        <form className={`p-fluid ${S.root}`} onSubmit={handleSubmit} data-testid="mcp-login-form">
          <Card className={T.bgElevation2}>
            <h2 className="atlas-h4">Connect your MCP client</h2>
            <p>
              {passwordLogin
                ? "Please enter your password to continue."
                : "Please enter your email address to continue."}
            </p>
          </Card>
          <InputText
            id="email"
            data-testid="mcp-login-email-input"
            className="w-full"
            type="email"
            name="email"
            value={email}
            onChange={e => onEmailChange(e.target.value)}
            placeholder="Email address"
            autoComplete="email"
            required
            aria-required="true"
            aria-label="Email address"
            aria-invalid={!!emailError}
            aria-describedby={emailError ? "mcp-login-email-error" : undefined}
          />
          {emailError && (
            <small className="p-error" id="mcp-login-email-error" role="alert">
              {emailError}
            </small>
          )}
          {passwordLogin && (
            <Password
              id="password"
              data-testid="mcp-login-password-input"
              className="w-full"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Password"
              required
              aria-required="true"
              aria-label="Password"
              toggleMask
              feedback={false}
              pt={{ hideIcon: { className: S.pwIcon }, showIcon: { className: S.pwIcon } }}
            />
          )}
          <Button
            className="w-full"
            data-testid="mcp-login-submit-button"
            type="submit"
            label="Sign In"
            disabled={isSubmitting}
            loading={isSubmitting}
          />
        </form>
      )}
    </LoginLayout>
  );
}

// `/login/mcp` serves two legs of the MCP OAuth flow: the email-first login handoff
// (`?state=`) and, for CIMD clients whose user belongs to several tenants, the tenant picker svc-idp redirects back to `?selection_id=`.
// The LaunchDarkly kill switch below applies to both.
export default function McpLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mcpOauthLoginFrontend } = useFlags();

  // If MCP OAuth login is disabled, send the user somewhere sensible instead of
  // a dead form: the app for authenticated users, the normal login page
  // otherwise. `undefined` means LaunchDarkly hasn't resolved the flag yet —
  // stay put rather than bouncing users out of an enabled flow.
  useEffect(() => {
    if (mcpOauthLoginFrontend !== false) return;
    const redirectAway = async () => {
      const isAuthenticated = await rehydrateUserAuthStatus();
      if (isAuthenticated) {
        window.location.href = "/";
      } else {
        navigate(LoginAppRoutes.route);
      }
    };
    redirectAway();
  }, [mcpOauthLoginFrontend, navigate]);

  const selectionId = searchParams.get("selection_id");
  return selectionId ? <McpTenantSelection selectionId={selectionId} /> : <McpEmailLogin />;
}
